import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getInstanceIdFromUrl } from "@/utils/instanceId";
import { logger } from "@/services/logger.service";
import type { IRootReducerState } from "@/store/IRootReducer";
import { getAccessToken, clearCachedToken, authService } from "@/utils/authService";
import { setAccessToken, signOut } from "@/store/slices/authSlice";
import { denialCodeOf, reportAccessDenial } from "@/components/access/accessDenial";
import { productOfApiUrl, reportProductDisabled } from "@/components/access/productRevocation";

const MAX_RETRIES = 3;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: "/api/v1",
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as IRootReducerState;
    const token = state.authReducer?.accessToken;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

// 401 is included so the retry middleware doesn't waste 3 attempts on auth
// failures — they go straight to baseQueryWithReauth which handles refresh.
//
// 429 is included because the server's window is a minute wide and this
// middleware backs off for 1s, 2s then 4s: every retry lands inside the same
// window, fails identically, and counts against it again. The only thing the
// three extra attempts bought was ~7s of delay before the error surfaced.
const NON_RETRYABLE_STATUSES = [400, 401, 403, 404, 409, 422, 429, 500, 503];

// Module-level flag so that when one query triggers a logout, all other
// in-flight 401s bail out instead of each trying to refresh and log out
// in parallel. Reset on the next sign-in (page reload clears it naturally).
let isLoggingOut = false;

const baseQueryWithBailout: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error) {
    const status = result.error.status;
    if (typeof status === "number" && NON_RETRYABLE_STATUSES.includes(status)) {
      retry.fail(result.error);
    }
  }
  return result;
};

const baseQueryWithRetry = retry(baseQueryWithBailout, {
  maxRetries: MAX_RETRIES,
  backoff: async (attempt, _maxRetries) => {
    const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
    await new Promise((resolve) => setTimeout(resolve, delay));
  },
});

// Wrapper that handles 401 by refreshing the B2C token and retrying once
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  // If a previous 401 already triggered logout, bail out immediately so we
  // don't pile up parallel refresh attempts and redundant logout calls.
  if (isLoggingOut) {
    return {
      error: {
        status: 401,
        data: "Logging out",
      } as FetchBaseQueryError,
    };
  }

  let result = await baseQueryWithRetry(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Another query may have started logout while we were awaiting our retry.
    if (isLoggingOut) return result;

    let refreshFailed = false;
    try {
      // Force a real refresh by clearing the in-memory token cache first.
      // Otherwise getAccessToken() may return a stale cached token that the
      // server will also reject, leading to an infinite 401 loop.
      clearCachedToken();
      const newToken = await getAccessToken();
      api.dispatch(setAccessToken(newToken));
      result = await baseQueryWithRetry(args, api, extraOptions);
      // If the retry STILL returns 401, the refreshed token is also invalid
      // (or refresh silently returned a stale token) → treat as refresh failure.
      if (result.error && result.error.status === 401) {
        refreshFailed = true;
      }
    } catch {
      refreshFailed = true;
    }

    if (refreshFailed && !isLoggingOut) {
      // Token refresh failed — the B2C session is dead. Clear auth state
      // and redirect to sign-in so the user isn't stuck in a broken session.
      // Set the flag immediately so concurrent queries bail out.
      isLoggingOut = true;
      clearCachedToken();
      api.dispatch(signOut());
      authService.logout().catch(() => {
        // If MSAL logout fails (popup blocked, etc.), force a hard redirect
        window.location.href = "/";
      });
    }
  }

  return result;
};

// Paths that don't require an instance ID (global endpoints)
const INSTANCE_ID_EXEMPT_PATHS = ["/admin/rbac", "/auth/"];

const isInstanceIdExempt = (url: string): boolean => {
  return INSTANCE_ID_EXEMPT_PATHS.some((path) => url.includes(path));
};

/**
 * Exempt paths that should still CARRY an instance id when one is known.
 *
 * `/admin/rbac` is exempt because these screens must keep working for a
 * tenant-scoped admin with no instance selected — dropping the request would
 * lock someone out of Role Management, which is the screen you would use to
 * repair roles, so the outage would seal itself shut.
 *
 * But exempt was implemented as "never send it", which meant the server could
 * never evaluate a permission for these routes: `requirePermission` needs an
 * instance to resolve a role against, so every gate here had to fall back to a
 * role check and the "Manage users" switch could not enforce anything.
 *
 * Sending it when we have it, and proceeding without it when we do not, gives
 * the server enough context to honour the switch in the normal case while
 * keeping the no-instance path exactly as forgiving as it was.
 */
const INSTANCE_ID_OPTIONAL_PATHS = ["/admin/rbac"];

const isInstanceIdOptional = (url: string): boolean => {
  return INSTANCE_ID_OPTIONAL_PATHS.some((path) => url.includes(path));
};

const baseQueryWithInstanceId: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const url = typeof args === "string" ? args : args.url;

  // Skip instance ID check for exempt paths (global endpoints)
  if (isInstanceIdExempt(url)) {
    const modifiedArgs: FetchArgs = typeof args === "string" ? { url: args } : { ...args };
    // Best effort: attach the instance when one is known, so the server can
    // evaluate a permission rather than falling back to a role check. Never
    // blocks the request when there is no instance — that is the whole point of
    // these paths being exempt.
    const known = isInstanceIdOptional(url) ? getInstanceIdFromUrl() : null;
    // Not when the caller already passes it in `params`: Express would read both as an array.
    const inParams =
      typeof args !== "string" &&
      typeof args.params?.instanceId === "string" &&
      args.params.instanceId !== "";
    if (known && !inParams && !modifiedArgs.url.toLowerCase().includes("instanceid=")) {
      const separator = modifiedArgs.url.includes("?") ? "&" : "?";
      modifiedArgs.url = `${modifiedArgs.url}${separator}instanceId=${known}`;
    }
    return baseQueryWithReauth(modifiedArgs, api, extraOptions);
  }

  const instanceId = getInstanceIdFromUrl();

  if (!instanceId) {
    logger.warn("[API] No instance ID available - API request may fail");
    return {
      error: {
        status: 400,
        statusText: "Bad Request",
        data: {
          error: "No instance selected",
          code: "MISSING_INSTANCE_ID",
          message:
            "Please select an instance to continue. Try refreshing the page or selecting a community from the navigation.",
        },
      } as FetchBaseQueryError,
    };
  }

  let modifiedArgs: FetchArgs;

  if (typeof args === "string") {
    const urlAlreadyHasInstanceId = args.toLowerCase().includes("instanceid=");
    if (urlAlreadyHasInstanceId) {
      modifiedArgs = { url: args };
    } else {
      const separator = args.includes("?") ? "&" : "?";
      modifiedArgs = {
        url: `${args}${separator}instanceId=${instanceId}`,
      };
    }
  } else {
    const url = args.url;
    const urlAlreadyHasInstanceId = url.toLowerCase().includes("instanceid=");
    const paramsInstanceId = args.params?.instanceId;
    const paramsHasValidInstanceId =
      typeof paramsInstanceId === "string" && paramsInstanceId.length > 0;
    if (urlAlreadyHasInstanceId || paramsHasValidInstanceId) {
      modifiedArgs = { ...args };
    } else {
      const separator = url.includes("?") ? "&" : "?";
      modifiedArgs = {
        ...args,
        url: `${url}${separator}instanceId=${instanceId}`,
      };
    }
  }

  return baseQueryWithReauth(modifiedArgs, api, extraOptions);
};

/**
 * Deny-by-default (Phase U): a 403 carrying a tenant-access code (NO_ASSIGNMENT,
 * ACCOUNT_BLOCKED, ...) means the user can't use this instance at all, not that one
 * action was refused. Reporting it swaps the whole app for the no-access screen
 * (AppNew), which unmounts everything that was asking or polling behind it.
 */
const baseQueryReportingDenial: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQueryWithInstanceId(args, api, extraOptions);
  if (result.error?.status === 403) {
    const code = denialCodeOf(result.error.data);
    if (code) reportAccessDenial({ code, instanceId: getInstanceIdFromUrl() });
  }
  // A product switched off mid-session (spec §6): only a call under that product's own prefix, and
  // only with the server's code. A hire 404 maps to no product, so it never fires.
  if (result.error?.status === 404) {
    const url = typeof args === "string" ? args : args.url;
    const product = productOfApiUrl(url);
    const code = (result.error.data as { code?: unknown } | undefined)?.code;
    if (product && code === "PRODUCT_DISABLED") reportProductDisabled(product);
  }
  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryReportingDenial,
  // Going back to a page shows what it had at once instead of a loader. Answers stay cached for
  // 5 minutes after the last page using them closes (RTK's default is 60 seconds), and one older
  // than 30 seconds is re-read in the background on return. Endpoints and hooks that set their
  // own values keep them.
  keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: 30,
  tagTypes: [
    "CandidateEngagement",
    "HrisConnection",
    "HrisRuns",
    "HrisRoster",
    // Who the HRIS feed is allowed to scope. Separate from HrisRoster: turning management on or
    // off for one person must not re-fetch the whole facility and department map.
    "HrisScope",
    // greytHR (Settings → greytHR). Categories are greytHR's own lists, cached server-side.
    "GreythrConnection",
    "GreythrMappings",
    "GreythrCategories",
    "GreythrOutbox",
    // A hire's greytHR plan (the Hired dialog's line): changed by approvals AND by mapping saves,
    // which re-park approved pushes without touching the applicant.
    "GreythrPlan",
    "CandidateEngagementRules",
    // Every connector row plus the catalog's "already added" flags. One tag for
    // both, because adding a connector changes the catalog tile in the same
    // breath and a stale tile offers an Add that 409s.
    "Connectors",
    "SkyNoteStatus",
    "SkyLearnOnboarding",
    "SkyLearnConfig",
    "SkyLearnSettings",
    "SkyNoteSettings",
    "SkyNoteMeeting",
    "ReportLayout",
    "Communities",
    "Scheduler",
    "Locations",
    "Operators",
    "Owners",
    "Templates",
    "Surveys",
    "SurveyRequests",
    "SurveyResponses",
    "SurveyTemplates",
    "Leads",
    "Lead",
    "CrmIntegrations",
    "CommunityPricing",
    "CommunityMedia",
    "CommunityAmenities",
    "InquiryTypes",
    "ModalConfigs",
    "AutoTriggers",
    "WidgetPlacements",
    "ModalTests",
    "ContentFolders",
    "ContentAssets",
    "LandingPages",
    "LandingPageSections",
    "LandingPageTemplates",
    "Tours",
    "Tour",
    "RBAC",
    "Credentials",
    "Onboard",
    "SkyAgent",
    "SkyAgentSettings",
    "SkyAgentSkills",
    "SkyAgentMemory",
    "SkyAgentQuickPrompts",
    "SkyAgentAssignments",
    "JobPortals",
    "Jobs",
    "Applicants",
    "JobPortalAssignments",
    "PortalJobs",
    "CustomRoles",
    "AccessUsers",
    "SeatUsage",
    "IndustryTemplates",
    "InstanceStatus",
    "InstanceIndustry",
    // Staff: an instance's products and their event history (spec §3.3; the Tenants panel).
    "InstanceProducts",
    "Interviews",
    "AiInterviews",
    "AiInterviewTemplates",
    "InterviewGuides",
    "InterviewGuideResponses",
    "EmailTemplates",
    "EmailTemplate",
    "SmsTemplates",
    "SmsTemplate",
    "EmailSignatures",
    "SenderEmails",
    "ApplicantNotes",
    "ApplicantFiles",
    "Documents",
    "FeedbackTemplates",
    "Feedback",
    "MicrosoftOAuth",
    // The instance's SharePoint connection and the caller's own Microsoft sign-in (one status call).
    "SharePoint",
    // The folder picker's levels. Separate from SharePoint: saving the default location must not
    // re-fetch the picker, but adding a site or creating a folder must.
    "SharePointBrowse",
    // The Document Centre. People is the list (no SharePoint call); the other three are keyed by
    // person, so acting on one candidate does not re-read SharePoint for every open folder.
    "CandidateDocumentPeople",
    "CandidateDocumentFolder",
    "CandidateDocumentRequests",
    "CandidateDocumentAudit",
    "PipelineStages",
    "ArchiveReasons",
    "InterviewerAssignments",
    "JobTeamAssignments",
    "AccessScope",
    "LocationGroups",
    "Departments",
    "UserScope",
    "JobsToFix",
    "IndeedIntegration",
    "LinkedInIntegration",
    "JobFollowers",
    "JobAccess",
    "MatchScore",
    "ResumeDigest",
    "JobPostingOptions",
    "JobTitles",
    "ScreeningQuestions",
    "BulkUploads",
    "EmailHistory",
    "EasyBookLinks",
    "RediscoveredCandidates",
    "FormattedResumes",
    "ResumeTemplates",
    "AnonymizationConfigs",
    "Offers",
    "OfferTemplates",
    "OfferWorkflows",
    "OfferApprovals",
    "OfferRevisions",
    "Clients",
    "ClientUsers",
    "ClientShares",
    "Referrals",
    "ReferralLinks",
    "ReferralHub",
    "ReferralOpenings",
    "MyReferrals",
    "ReferralRewards",
    // skyPerformance's own endpoints (src/products/performance/api); hire never provides it.
    "Performance",
    // skyPerformance IT's own endpoints (src/products/performance-it/api); nobody else provides or
    // invalidates it, and its slice touches no other tag.
    "PerformanceIT",
    // Employee onboarding (src/store/api/onboardingApi.ts). Cases and approvals are separate tags
    // because a task decision changes both, while a template edit changes neither.
    "OnboardingCases",
    "OnboardingCase",
    "OnboardingApprovals",
    "OnboardingTemplates",
    "OnboardingSettings",
    "OnboardingEligible",
    "OnboardingActions",
  ],
  endpoints: () => ({}),
});

export default baseApi;
